import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  ArrowUpRight,
  ArrowRight,
  ArrowDown,
  Check,
  CheckCheck,
  ChevronDown,
  Download,
  Heart,
  Search,
  X,
  Play,
  Pause,
  Smartphone,
  ShieldCheck,
  SlidersHorizontal,
  Menu,
  Sun,
  Image as ImageIcon,
  CircleHelp,
  LockKeyhole,
  Film,
  LoaderCircle,
  RefreshCw,
  Copy,
  Monitor,
  Aperture,
  Grid2X2,
  Camera,
  Flashlight,
  CheckCircle2,
  UserRound,
} from 'lucide-react';
import '@fontsource-variable/manrope';
import '@fontsource-variable/noto-sans-sc';
import type { Wallpaper, PaymentMode, AccountUser, AuthValues } from './types';
import { useAppNavigation } from './hooks/useAppNavigation';
import { Brand } from './components/Brand';
import { PageLink } from './components/PageLink';
import { Dialog } from './components/Dialog';
import {
  AccountMenu,
  AuthDialog,
  AccountSettingsDialog,
  AccountAccessGate,
} from './components/AccountUI';
import { useAccountData } from './hooks/useAccountData';
import { PersonalCollectionPage } from './components/PersonalCollectionPage';
import { Checkout } from './components/Checkout';
import { OrderRecoveryDialog } from './components/OrderRecoveryDialog';
import { api } from './lib/api';
import { downloadWallpaper, type DownloadFormat } from './lib/downloadWallpaper';
import { SUPABASE_CHECKOUT, SUPABASE_ENVIRONMENT } from './lib/supabase';
import { ArtMedia } from './components/ArtMedia';
import { EditorialHero, MotionStudy, Stories, Manifesto } from './components/Editorial';
import { useReducedMotion } from './hooks/useReducedMotion';
import { screenDate } from './lib/screenDate';
import './style.css';
import './detail.css';
import './cover.css';
import './readability.css';
import './personal.css';
import './account.css';
import './brand.css';

type Info = 'about' | 'guide' | 'license' | 'privacy' | null;
const categories = [
  '全部作品',
  '抽象艺术',
  '自然意境',
  '极简主义',
  '流光渐变',
  '宇宙漫游',
  '3D 质感',
];
const money = (price: number) => (price === 0 ? '免费下载' : `¥${price}`);
type ToastAction = { label: string; onClick: () => void };
function Toast({
  message,
  onClose,
  action,
}: {
  message: string;
  onClose: () => void;
  action?: ToastAction | null;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = ref.current!;
    element.showPopover();
    return () => {
      if (element.matches(':popover-open')) element.hidePopover();
    };
  }, []);
  return (
    <div ref={ref} popover="manual" className="toast" role="status">
      <CheckCircle2 size={18} />
      <span>{message}</span>
      {action && (
        <button className="toast-action" onClick={action.onClick}>
          {action.label}
        </button>
      )}
      <button onClick={onClose} aria-label="关闭提示">
        <X size={14} />
      </button>
    </div>
  );
}
function App() {
  const { page, hash, locationKey, navigate } = useAppNavigation();
  const [category, setCategory] = useState('全部作品');
  const [type, setType] = useState('all');
  const [sort, setSort] = useState('curated');
  const [layout, setLayout] = useState<'editorial' | 'grid'>('editorial');
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [collection, setCollection] = useState('');
  const [freeOnly, setFreeOnly] = useState(false);
  const [limit, setLimit] = useState(8);
  const [selected, setSelected] = useState<Wallpaper | null>(null);
  const [info, setInfo] = useState<Info>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [toast, setToast] = useState('');
  const [toastAction, setToastAction] = useState<ToastAction | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [pendingCheckout, setPendingCheckout] = useState('');
  const [authView, setAuthView] = useState<'login' | 'register' | null>(null);
  const [accountSettings, setAccountSettings] = useState(false);
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const authReturnItem = useRef<string | null>(null);
  const gallery = useRef<HTMLElement>(null);
  const hasHandledQuery = useRef(false);
  const notify = (message: string, action: ToastAction | null = null) => {
    setToast(message);
    setToastAction(action);
  };
  const account = useAccountData(notify);
  const {
    items,
    paymentMode: mode,
    user,
    favorites,
    loading,
    error,
    loadCatalog,
    toggleFavorite,
  } = account;
  const previousUser = useRef<string | null>(null);
  useEffect(() => {
    const next = user?.id ?? null;
    if (previousUser.current !== next) {
      setToastAction(null);
      if (previousUser.current) {
        setSelected(null);
        setAccountSettings(false);
      }
      if (next) setAuthView(null);
      previousUser.current = next;
    }
  }, [user?.id]);
  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    setSelected(null);
    setInfo(null);
    setAuthView(null);
    setAccountSettings(false);
    authReturnItem.current = null;
  }, [locationKey]);
  async function verifyCheckout(sessionId: string) {
    setVerifying(true);
    try {
      const data = await api<{ unlocked: boolean; id: string }>('/api/checkout/verify', {
        method: 'POST',
        body: JSON.stringify({ sessionId }),
      });
      if (data.unlocked) {
        const fresh = await loadCatalog();
        setSelected(fresh.find((item) => item.id === data.id) ?? null);
        notify('支付已核实，高清作品已解锁。');
        setPendingCheckout('');
        history.replaceState(null, '', location.pathname);
      } else {
        setPendingCheckout(sessionId);
        notify('支付尚在处理中，请稍后重新核实。');
      }
    } catch (e) {
      setPendingCheckout(sessionId);
      notify((e as Error).message);
    } finally {
      setVerifying(false);
    }
  }
  useEffect(() => {
    loadCatalog().then((data) => {
      if (hasHandledQuery.current || !data.length) return;
      hasHandledQuery.current = true;
      const params = new URLSearchParams(location.search);
      if (params.get('checkout')) void verifyCheckout(params.get('checkout')!);
      else if (params.get('wallpaper'))
        setSelected(data.find((item) => item.id === params.get('wallpaper')) ?? null);
      if (params.has('cancelled')) notify('支付已取消，没有扣款；你可以继续浏览。');
    });
  }, []);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(
      () => {
        setToast('');
        setToastAction(null);
      },
      toastAction ? 8000 : 4500,
    );
    return () => clearTimeout(timer);
  }, [toast, toastAction]);
  useEffect(() => {
    setLimit(8);
  }, [category, type, query, collection, freeOnly, sort]);
  const scrollGallery = () => {
    setMenuOpen(false);
    navigate('/#discover');
  };
  function resetFilters() {
    setCategory('全部作品');
    setType('all');
    setQuery('');
    setCollection('');
    setFreeOnly(false);
    setSort('curated');
  }
  function discover() {
    resetFilters();
    scrollGallery();
  }
  function showDynamic() {
    resetFilters();
    setType('dynamic');
    scrollGallery();
  }
  function removeFavorite(item: Wallpaper) {
    const index = favorites.indexOf(item.id);
    const owner = account.currentUserId();
    account.saveFavorite(item.id, false);
    notify(`已取消收藏「${item.title}」`, {
      label: '撤销',
      onClick: () => {
        if (owner !== account.currentUserId()) {
          notify('账号已切换，请在原账号中操作。');
          return;
        }
        account.saveFavorite(item.id, true, index);
        notify('已恢复收藏。');
      },
    });
  }
  function requestLogin(itemId?: string) {
    authReturnItem.current = itemId ?? null;
    setSelected(null);
    setInfo(null);
    setMenuOpen(false);
    setSearchOpen(false);
    setAuthView('login');
  }
  async function handleAuthentication(kind: 'login' | 'register', values: AuthValues) {
    const result = await account.authenticate(kind, values);
    if (result.status === 'confirm-email') return result;
    setAuthView(null);
    if (authReturnItem.current) {
      setSelected(account.items.find((item) => item.id === authReturnItem.current) ?? null);
      authReturnItem.current = null;
    }
    notify(
      `${kind === 'register' ? '账号已创建' : '欢迎回来'}，${result.user?.displayName ?? ''}。${
        result.mergedFavorites ? '本机收藏已合并到账号。' : '收藏已同步。'
      }`,
    );
    return result;
  }
  async function handleLogout() {
    try {
      await account.logout();
      setSelected(null);
      setAccountSettings(false);
      setToastAction(null);
      resetFilters();
      navigate('/');
      notify('已退出登录，账号中的作品会为你保留。');
    } catch (error) {
      notify((error as Error).message);
    }
  }
  function searchCatalog() {
    const keyword = query;
    resetFilters();
    setQuery(keyword);
    setSearchOpen(false);
    scrollGallery();
  }
  async function downloadFromLibrary(item: Wallpaper, format: DownloadFormat) {
    await downloadWallpaper(item, format);
    notify('文件已交给浏览器保存，请查看下载记录。');
  }
  const favoriteCount = items.filter((item) => favorites.includes(item.id)).length;
  const results = useMemo(() => {
    const filtered = items.filter(
      (item) =>
        (category === '全部作品' || item.category === category) &&
        (type === 'all' || item.type === type) &&
        (!collection || item.collection === collection) &&
        (!freeOnly || item.price === 0) &&
        (!query ||
          `${item.title} ${item.english} ${item.category} ${item.description}`
            .toLowerCase()
            .includes(query.toLowerCase().trim())),
    );
    if (sort === 'price-low') filtered.sort((a, b) => a.price - b.price);
    if (sort === 'price-high') filtered.sort((a, b) => b.price - a.price);
    if (sort === 'newest') filtered.sort((a, b) => Number(b.fresh) - Number(a.fresh));
    return filtered;
  }, [items, category, type, collection, freeOnly, query, sort]);
  const activeFilter = category !== '全部作品' || type !== 'all' || collection || freeOnly || query;
  return (
    <>
      <header className="site-header">
        <div className="header-inner">
          <PageLink
            href="/"
            navigate={navigate}
            className="logo-link"
            aria-label="幕间首页"
            onClick={resetFilters}
          >
            <Brand />
          </PageLink>
          <nav className="desktop-nav" aria-label="主导航">
            <button
              className={
                page === 'home' && hash !== '#collections' && type !== 'dynamic' ? 'nav-active' : ''
              }
              onClick={discover}
            >
              发现壁纸
            </button>
            <PageLink
              href="/#collections"
              navigate={navigate}
              className={page === 'home' && hash === '#collections' ? 'nav-active' : ''}
            >
              灵感合集
            </PageLink>
            <button
              className={
                page === 'home' && hash !== '#collections' && type === 'dynamic' ? 'nav-active' : ''
              }
              onClick={showDynamic}
            >
              动态壁纸<span className="tiny-new">NEW</span>
            </button>
            <button onClick={() => setInfo('about')}>关于幕间</button>
          </nav>
          <div className="header-actions">
            <button
              className={`icon-button global-search-toggle ${searchOpen ? 'is-active' : ''}`}
              onClick={() => setSearchOpen(!searchOpen)}
              aria-label="搜索壁纸"
              aria-expanded={searchOpen}
            >
              <Search size={19} />
            </button>
            <span className="header-divider" />
            <AccountMenu
              page={page}
              favoriteCount={favoriteCount}
              ownedCount={items.filter((item) => item.owned).length}
              user={user}
              loading={loading}
              busy={account.authBusy}
              onLogin={() => requestLogin()}
              onLogout={handleLogout}
              onSettings={() => setAccountSettings(true)}
              navigate={navigate}
            />
            <button
              className="icon-button mobile-menu"
              aria-label="打开导航菜单"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <X size={21} /> : <Menu size={21} />}
            </button>
          </div>
        </div>
        {searchOpen && (
          <div className="header-search">
            <Search size={19} />
            <input
              autoFocus
              aria-label="搜索作品名称或灵感"
              placeholder="搜索作品、颜色或一点灵感…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  searchCatalog();
                }
                if (e.key === 'Escape') setSearchOpen(false);
              }}
            />
            <button
              onClick={() => {
                searchCatalog();
              }}
            >
              搜索 <ArrowRight size={16} />
            </button>
          </div>
        )}
        {menuOpen && (
          <nav className="mobile-nav">
            <button
              onClick={() => {
                setMenuOpen(false);
                setSearchOpen(true);
              }}
            >
              搜索壁纸
            </button>
            <button onClick={discover}>发现壁纸</button>
            <PageLink href="/#collections" navigate={navigate}>
              灵感合集
            </PageLink>
            <button onClick={showDynamic}>动态壁纸</button>
            <button
              onClick={() => {
                setInfo('about');
                setMenuOpen(false);
              }}
            >
              关于幕间
            </button>
          </nav>
        )}
      </header>
      <main id="main-content">
        {page === 'home' ? (
          <>
            <EditorialHero
              items={items}
              onDiscover={discover}
              onOpen={setSelected}
              suspended={Boolean(
                selected || info || authView || accountSettings || menuOpen || searchOpen,
              )}
            />
            {pendingCheckout && (
              <div className="payment-banner page-width">
                <span>订单支付状态待核实。请勿重复购买。</span>
                <button onClick={() => verifyCheckout(pendingCheckout)} disabled={verifying}>
                  <RefreshCw size={15} className={verifying ? 'spin' : ''} />
                  重新核实
                </button>
              </div>
            )}
            <section className="gallery page-width" ref={gallery} id="discover">
              <div className="section-heading">
                <div>
                  <div className="section-kicker">精选壁纸</div>
                  <h2>
                    好看的，不必千篇一律。
                    <span className="orange-period" />
                  </h2>
                </div>
                <p>不追逐更多，只挑真正打动你的那一幅。</p>
              </div>
              <div className="gallery-filters">
                <div className="category-tabs" aria-label="壁纸分类">
                  {categories.map((name, index) => (
                    <button
                      key={name}
                      className={category === name ? 'active' : ''}
                      onClick={() => setCategory(name)}
                      aria-pressed={category === name}
                    >
                      {name}
                      {index === 0 && <span>{items.length}</span>}
                    </button>
                  ))}
                </div>
                <div className="filter-secondary">
                  <div className="type-switch" aria-label="壁纸类型">
                    <button
                      className={type === 'all' ? 'active' : ''}
                      onClick={() => setType('all')}
                      aria-pressed={type === 'all'}
                    >
                      全部
                    </button>
                    <button
                      className={type === 'static' ? 'active' : ''}
                      onClick={() => setType('static')}
                      aria-pressed={type === 'static'}
                    >
                      <ImageIcon size={13} />
                      静态
                    </button>
                    <button
                      className={type === 'dynamic' ? 'active' : ''}
                      onClick={() => setType('dynamic')}
                      aria-pressed={type === 'dynamic'}
                    >
                      <Play size={12} />
                      动态
                    </button>
                  </div>
                  <div className="sort-controls">
                    <div className="layout-switch" aria-label="画廊布局">
                      <button
                        className={layout === 'editorial' ? 'active' : ''}
                        onClick={() => setLayout('editorial')}
                        aria-pressed={layout === 'editorial'}
                        aria-label="策展视图"
                      >
                        <span className="layout-symbol editorial-symbol" /> <span>策展</span>
                      </button>
                      <button
                        className={layout === 'grid' ? 'active' : ''}
                        onClick={() => setLayout('grid')}
                        aria-pressed={layout === 'grid'}
                        aria-label="网格视图"
                      >
                        <Grid2X2 size={14} /> <span>网格</span>
                      </button>
                    </div>
                    <button
                      className={`free-filter ${freeOnly ? 'active' : ''}`}
                      onClick={() => setFreeOnly(!freeOnly)}
                      aria-pressed={freeOnly}
                    >
                      <SlidersHorizontal size={14} />
                      {freeOnly ? '仅看免费' : '免费专区'}
                    </button>
                    <span />
                    <div className="select-wrap">
                      <select
                        value={sort}
                        onChange={(e) => setSort(e.target.value)}
                        aria-label="作品排序"
                      >
                        <option value="curated">编辑精选</option>
                        <option value="newest">新品优先</option>
                        <option value="price-low">价格从低到高</option>
                        <option value="price-high">价格从高到低</option>
                      </select>
                      <ChevronDown size={13} />
                    </div>
                  </div>
                </div>
              </div>
              {!!activeFilter && (
                <div className="filter-summary">
                  <span>
                    找到 {results.length} 幅作品{collection && ` · ${collection}`}
                    {query && ` · “${query}”`}
                  </span>
                  <button onClick={resetFilters}>
                    清除筛选 <X size={13} />
                  </button>
                </div>
              )}
              {loading ? (
                <div className="gallery-grid">
                  {Array.from({ length: 8 }, (_, i) => (
                    <div className="skeleton-card" key={i}>
                      <div />
                      <span />
                      <small />
                    </div>
                  ))}
                </div>
              ) : error ? (
                <div className="empty-state">
                  <CircleHelp size={32} />
                  <h3>画廊暂时没有打开</h3>
                  <p>{error}</p>
                  <button className="button button-dark" onClick={loadCatalog}>
                    重新加载 <RefreshCw size={16} />
                  </button>
                </div>
              ) : results.length === 0 ? (
                <div className="empty-state">
                  <Search size={35} />
                  <h3>换个词，也许灵感就来了。</h3>
                  <p>没有找到匹配的作品，试试“自然”或“流光”。</p>
                  <button className="button button-dark" onClick={resetFilters}>
                    探索全部作品 <ArrowRight size={16} />
                  </button>
                </div>
              ) : (
                <div
                  className={`gallery-grid ${layout === 'editorial' && !activeFilter && sort === 'curated' ? 'editorial-grid' : ''}`}
                >
                  {results.slice(0, limit).map((item, index) => (
                    <WallpaperCard
                      key={item.id}
                      item={item}
                      index={index}
                      favorite={favorites.includes(item.id)}
                      onFavorite={user ? () => toggleFavorite(item.id) : undefined}
                      onOpen={() => setSelected(item)}
                    />
                  ))}
                  {layout === 'editorial' &&
                    !activeFilter &&
                    sort === 'curated' &&
                    Math.min(limit, results.length) === 8 && (
                      <aside className="gallery-interlude">
                        <span className="interlude-star" aria-hidden="true">
                          ✳
                        </span>
                        <span className="section-kicker">遇见新灵感</span>
                        <h3>
                          不如，
                          <br />
                          偶遇一份喜欢。
                        </h3>
                        <button
                          className="text-button"
                          onClick={() => {
                            if (items.length)
                              setSelected(items[Math.floor(Math.random() * items.length)]);
                          }}
                        >
                          给我一点惊喜 <ArrowUpRight size={19} />
                        </button>
                      </aside>
                    )}
                </div>
              )}
              {!loading && !error && results.length > 0 && (
                <div className="gallery-bottom">
                  <span>每一份灵感，都值得认真对待。</span>
                  {limit < results.length ? (
                    <button className="button button-outline" onClick={() => setLimit(limit + 8)}>
                      再发现一些美好 <ArrowDown size={15} />
                    </button>
                  ) : (
                    <button
                      className="all-seen"
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    >
                      <CheckCheck size={16} />
                      已经看完所有灵感 <ArrowUpRight size={14} />
                    </button>
                  )}
                  <span>
                    已展示 {Math.min(limit, results.length)} / {results.length} 幅
                  </span>
                </div>
              )}
            </section>
            <MotionStudy
              item={items.find((item) => item.id === 'azure')}
              onExplore={showDynamic}
              onOpen={setSelected}
            />
            <Stories
              items={items}
              onSelect={(name) => {
                resetFilters();
                setCollection(name);
                scrollGallery();
              }}
            />
            <Manifesto onAbout={() => setInfo('about')} />
          </>
        ) : user ? (
          <PersonalCollectionPage
            key={`${page}:${user.id}`}
            page={page}
            user={user}
            favoriteSyncing={account.favoriteSyncing}
            onRefresh={loadCatalog}
            items={items}
            favorites={favorites}
            paymentMode={mode}
            loading={loading}
            error={error}
            navigate={navigate}
            onOpen={setSelected}
            onRemoveFavorite={removeFavorite}
            onDownload={downloadFromLibrary}
            onRetry={loadCatalog}
            onHelp={() => setInfo('guide')}
          />
        ) : (
          <AccountAccessGate
            loading={loading}
            error={error}
            onRetry={loadCatalog}
            onLogin={() => requestLogin()}
            navigate={navigate}
          />
        )}
      </main>
      <footer className={page === 'home' ? '' : 'personal-footer'}>
        {page === 'home' && (
          <div className="footer-masthead page-width" aria-hidden="true">
            <span>MUJIAN</span>
            <span className="masthead-star">✳</span>
          </div>
        )}
        <div className="footer-main page-width">
          <div>
            <Brand />
            <p>给日常一点灵感，给屏幕一点艺术。</p>
          </div>
          <div className="footer-links">
            <button onClick={() => setInfo('guide')}>使用指南</button>
            <button onClick={() => setInfo('license')}>授权说明</button>
            <button onClick={() => setInfo('privacy')}>隐私说明</button>
            <button onClick={() => setRecoveryOpen(true)}>找回订单</button>
            <button onClick={() => setInfo('about')}>
              关于幕间 <ArrowUpRight size={12} />
            </button>
          </div>
        </div>
        <div className="footer-bottom page-width">
          <span>© 2026 幕间 MUJIAN</span>
          <span>
            <span className="status-dot" />
            {mode === 'demo'
              ? '体验模式 · 不会产生扣款'
              : mode === 'test'
                ? '支付测试模式 · 不会产生扣款'
                : '安全支付 · 原创设计'}
          </span>
          <span>
            一点灵感，无限可能 <Sun size={12} />
          </span>
        </div>
      </footer>
      {selected && (
        <WallpaperDetail
          key={selected.id}
          item={items.find((item) => item.id === selected.id) ?? selected}
          mode={mode}
          user={user}
          onLogin={() => requestLogin(selected.id)}
          favorite={favorites.includes(selected.id)}
          onFavorite={() => toggleFavorite(selected.id)}
          onClose={() => setSelected(null)}
          notify={notify}
          onUnlocked={async () => {
            await loadCatalog();
          }}
        />
      )}
      {info && <InfoDialog info={info} close={() => setInfo(null)} />}
      {(authView && !user) || account.recovery ? (
        <AuthDialog
          initialMode={authView ?? 'login'}
          recovery={account.recovery}
          busy={account.authBusy}
          guestFavorites={favoriteCount}
          guestOwned={items.filter((item) => item.owned).length}
          onClose={() => {
            setAuthView(null);
            account.clearRecovery();
            authReturnItem.current = null;
          }}
          onSubmit={handleAuthentication}
          onReset={account.requestReset}
          onUpdatePassword={account.updatePassword}
          onResend={account.verifyEmail}
        />
      ) : null}
      {accountSettings && user && (
        <AccountSettingsDialog
          user={user}
          busy={account.authBusy}
          onClose={() => setAccountSettings(false)}
          onProfile={account.updateProfile}
          onPassword={account.changePassword}
          onVerify={account.verifyEmail}
          notify={notify}
        />
      )}
      {recoveryOpen && (
        <OrderRecoveryDialog
          user={user}
          items={items}
          environment={SUPABASE_ENVIRONMENT}
          onClose={() => setRecoveryOpen(false)}
          onUnlocked={async () => {
            await loadCatalog();
          }}
          onLogin={() => {
            setRecoveryOpen(false);
            requestLogin();
          }}
          notify={notify}
        />
      )}

      {toast && (
        <Toast
          message={toast}
          action={toastAction}
          onClose={() => {
            setToast('');
            setToastAction(null);
          }}
        />
      )}
      {verifying && (
        <div className="verify-indicator" role="status">
          <LoaderCircle className="spin" size={17} />
          正在向支付服务核实订单…
        </div>
      )}
    </>
  );
}
function WallpaperCard({
  item,
  index,
  favorite,
  onFavorite,
  onOpen,
}: {
  item: Wallpaper;
  index: number;
  favorite: boolean;
  onFavorite?: () => void;
  onOpen: () => void;
}) {
  const [hover, setHover] = useState(false);
  const reduced = useReducedMotion();
  return (
    <article
      className="wallpaper-card"
      data-artwork={item.id}
      style={{ '--card-index': index % 4 } as React.CSSProperties}
    >
      <div
        className="card-art"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <button
          className="art-open"
          onClick={onOpen}
          aria-label={`预览${item.title}`}
          onFocus={() => setHover(true)}
          onBlur={() => setHover(false)}
        >
          <ArtMedia item={item} motion={hover && !reduced} />
          <span className="card-exhibition-title" aria-hidden="true">
            {item.english.replaceAll(' ', '\n')}
          </span>
          <span className="card-preview">
            <Smartphone size={16} />
            预览这份灵感 <ArrowUpRight size={16} />
          </span>
        </button>
        <div className="card-badges">
          <span className={`type-badge ${item.type === 'dynamic' ? 'motion-badge' : ''}`}>
            {item.type === 'dynamic' ? (
              <>
                <Play size={10} fill="currentColor" />
                动态
              </>
            ) : (
              <>4K · 静态</>
            )}
          </span>
          {item.fresh && <span className="new-badge">NEW</span>}
        </div>
        {onFavorite && (
          <button
            className={`favorite-button ${favorite ? 'favorited' : ''}`}
            aria-label={`${favorite ? '取消收藏' : '收藏'}${item.title}`}
            aria-pressed={favorite}
            onClick={onFavorite}
          >
            <Heart size={16} fill={favorite ? 'currentColor' : 'none'} />
          </button>
        )}
        <div className="art-bottom-caption">
          <span>MUJIAN</span>
        </div>
      </div>
      <div className="card-info">
        <div>
          <button className="card-title" onClick={onOpen}>
            {item.title}
          </button>
          <p>
            <span>{item.category}</span>
            <span className="card-size">
              {item.type === 'dynamic' ? '动态与静态' : '2160 × 4680'}
            </span>
          </p>
        </div>
        <button
          className={`card-price ${item.price === 0 ? 'free-price' : ''} ${item.owned ? 'owned-price' : ''}`}
          onClick={onOpen}
        >
          {item.owned ? (
            <>
              <Check size={13} />
              已解锁
            </>
          ) : (
            money(item.price)
          )}
        </button>
      </div>
    </article>
  );
}
function WallpaperDetail({
  item,
  mode,
  user,
  onLogin,
  favorite,
  onFavorite,
  onClose,
  notify,
  onUnlocked,
}: {
  item: Wallpaper;
  mode: PaymentMode;
  user: AccountUser | null;
  onLogin: () => void;
  favorite: boolean;
  onFavorite: () => void;
  onClose: () => void;
  notify: (s: string) => void;
  onUnlocked: () => Promise<void>;
}) {
  const [motion, setMotion] = useState(
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  const [preview, setPreview] = useState('lock');
  const [checkout, setCheckout] = useState(false);
  const [consent, setConsent] = useState(false);
  const [dynamicConsent, setDynamicConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [downloadBusy, setDownloadBusy] = useState('');
  const [failure, setFailure] = useState('');
  const accessible = item.owned || item.price === 0;
  async function purchase() {
    setBusy(true);
    setFailure('');
    try {
      const result = await api<{ url?: string; unlocked?: boolean; demo?: boolean }>(
        '/api/checkout',
        { method: 'POST', body: JSON.stringify({ id: item.id, demoConsent: consent }) },
      );
      if (result.url) {
        window.location.assign(result.url);
        return;
      }
      await onUnlocked();
      setCheckout(false);
      notify(
        result.demo
          ? '作品已解锁，本次体验未扣款。现在可以下载高清原图。'
          : '作品已解锁，可以下载。',
      );
    } catch (e) {
      setFailure((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function download(format: DownloadFormat) {
    setDownloadBusy(format);
    setFailure('');
    try {
      const result = await downloadWallpaper(item, format);
      if (result.addedToLibrary) await onUnlocked();
      notify('文件已交给浏览器保存，请查看下载记录。');
    } catch (error) {
      setFailure((error as Error).message);
    } finally {
      setDownloadBusy('');
    }
  }
  async function share() {
    try {
      await navigator.clipboard.writeText(`${location.origin}/?wallpaper=${item.id}`);
      notify('作品链接已复制，分享这份灵感吧。');
    } catch {
      notify('浏览器未允许复制，请从地址栏访问 ?wallpaper=' + item.id);
    }
  }
  return (
    <Dialog onClose={onClose} className="detail-dialog" title={`${item.title}壁纸详情`}>
      <div
        className="detail-preview"
        style={{ '--preview-color': item.palette[2] } as React.CSSProperties}
      >
        <div className="preview-label">
          <span className="status-dot" />
          锁屏效果预览<span>9 : 19.5</span>
        </div>
        <div className={`preview-phone ${preview === 'art' ? 'art-only' : ''}`}>
          <div className="preview-screen">
            <ArtMedia item={item} motion={motion} />
            {preview !== 'art' && (
              <>
                <span className="dynamic-island" />
                {preview === 'lock' ? (
                  <>
                    <div className="phone-clock">
                      <span>{screenDate.zh}</span>
                      <strong>9:41</strong>
                    </div>
                    <div className="phone-bottom">
                      <span>
                        <Flashlight size={16} />
                      </span>
                      <span>
                        <Camera size={16} />
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="home-screen">
                    <div className="home-widget">
                      <span>{screenDate.weekday}</span>
                      <strong>{screenDate.day}</strong>
                      <span>留一点时间给自己。</span>
                    </div>
                    <div className="home-icons">
                      {[Camera, Sun, Heart, ImageIcon, Monitor, Aperture, Film, Grid2X2].map(
                        (Icon, i) => (
                          <span key={i}>
                            <Icon size={23} />
                          </span>
                        ),
                      )}
                    </div>
                  </div>
                )}
                <span className="home-indicator" />
              </>
            )}
          </div>
        </div>
        <div className="preview-options">
          <button className={preview === 'lock' ? 'active' : ''} onClick={() => setPreview('lock')}>
            锁屏
          </button>
          <button className={preview === 'home' ? 'active' : ''} onClick={() => setPreview('home')}>
            主屏
          </button>
          <button className={preview === 'art' ? 'active' : ''} onClick={() => setPreview('art')}>
            原画
          </button>
          {item.type === 'dynamic' && (
            <button
              className="preview-play"
              onClick={() => setMotion(!motion)}
              aria-label={motion ? '暂停动态预览' : '播放动态预览'}
            >
              {motion ? <Pause size={13} /> : <Play size={13} />}
            </button>
          )}
        </div>
        <span className="preview-footnote">界面元素仅用于预览，不包含在下载原图中</span>
      </div>
      <div className="detail-content">
        <div className="detail-kicker">幕间原创</div>
        <span className="detail-category">
          {item.category} / {item.collection}
        </span>
        <h2>{item.title}</h2>
        <span className="detail-english">{item.english}</span>
        <p className="detail-description">{item.description}</p>
        <div className="palette">
          {item.palette.map((color) => (
            <span key={color} style={{ background: color }} title={color} />
          ))}
          <span>从色彩，开始一段灵感。</span>
        </div>
        <div className="spec-grid">
          <div>
            <ImageIcon size={17} />
            <span>
              静态原图<strong>2160 × 4680 · PNG</strong>
            </span>
          </div>
          <div>
            {item.type === 'dynamic' ? <Film size={17} /> : <Smartphone size={17} />}
            <span>
              {item.type === 'dynamic' ? '动态视频' : '屏幕比例'}
              <strong>{item.type === 'dynamic' ? '1080 × 2340 · MP4' : '9 : 19.5 · 竖屏'}</strong>
            </span>
          </div>
          <div>
            <ShieldCheck size={17} />
            <span>
              使用授权<strong>个人使用 · 不可转售</strong>
            </span>
          </div>
          <div>
            <Download size={17} />
            <span>
              获取方式<strong>一次解锁 · 原文件下载</strong>
            </span>
          </div>
        </div>
        {item.type === 'dynamic' && (
          <div className="live-notice">
            <CircleHelp size={17} />
            <p>
              <strong>下载格式说明</strong>包含 4 秒循环 MP4 和高清静态 PNG。设置 iPhone
              动态锁屏需另行转换为兼容的 Live Photo；设备与系统效果可能不同，不保证转换后可用。
            </p>
          </div>
        )}
        {failure && (
          <div className="inline-error" role="alert">
            {failure}
          </div>
        )}
        {checkout ? (
          SUPABASE_CHECKOUT === 'sandbox' || SUPABASE_CHECKOUT === 'live' ? (
            <Checkout
              item={item}
              user={user}
              mode={SUPABASE_CHECKOUT}
              onCancel={() => setCheckout(false)}
              onPaid={onUnlocked}
              notify={notify}
            />
          ) : (
            <div className="checkout-panel">
              <div>
                <strong>确认解锁</strong>
                <button aria-label="返回详情" onClick={() => setCheckout(false)}>
                  <X size={17} />
                </button>
              </div>
              <p>
                {mode === 'demo'
                  ? '当前为体验模式，本次解锁不收取费用。'
                  : mode === 'test'
                    ? '当前为支付测试模式，不会产生真实扣款。'
                    : '将跳转至 Stripe 安全结算页面，支付完成后解锁。'}
              </p>
              <div className="checkout-total">
                <span>{item.title} · 个人授权</span>
                <strong>{money(item.price)}</strong>
              </div>
              {mode === 'demo' && (
                <label className="consent">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                  />
                  我了解本次为免费体验，不会产生扣款。
                </label>
              )}
              {item.type === 'dynamic' && (
                <label className="consent">
                  <input
                    type="checkbox"
                    checked={dynamicConsent}
                    onChange={(e) => setDynamicConsent(e.target.checked)}
                  />
                  我了解下载的是 MP4 素材，而非可直接设置的 Live Photo。
                </label>
              )}
              <button
                className="button button-dark full-button"
                disabled={
                  busy ||
                  (mode === 'demo' && !consent) ||
                  (item.type === 'dynamic' && !dynamicConsent)
                }
                onClick={purchase}
              >
                {busy ? <LoaderCircle size={17} className="spin" /> : <LockKeyhole size={16} />}
                {mode === 'demo'
                  ? '免费体验并下载'
                  : mode === 'test'
                    ? '前往测试结算'
                    : `安全支付 ${money(item.price)}`}
              </button>
            </div>
          )
        ) : (
          <div className="detail-purchase">
            <div className="detail-price">
              <strong>{accessible ? (item.owned ? '已解锁' : '免费') : money(item.price)}</strong>
              <span>
                {item.owned
                  ? mode === 'demo'
                    ? '体验已解锁 · 未产生扣款'
                    : '感谢你支持原创设计'
                  : item.price === 0
                    ? '一份来自幕间的小礼物'
                    : '一次购买，不订阅'}
              </span>
            </div>
            <div className="detail-actions">
              <button
                className="button button-dark"
                disabled={!!downloadBusy}
                onClick={() => (accessible ? download('png') : setCheckout(true))}
              >
                {downloadBusy === 'png' ? (
                  <LoaderCircle size={17} className="spin" />
                ) : accessible ? (
                  <Download size={17} />
                ) : (
                  <LockKeyhole size={16} />
                )}
                {accessible ? '下载高清 PNG' : mode === 'demo' ? '体验解锁' : '购买并下载'}
                {!accessible && <ArrowUpRight size={17} />}
              </button>
              {user && (
                <button
                  className={`button button-outline detail-heart ${favorite ? 'favorited' : ''}`}
                  onClick={onFavorite}
                  aria-label={favorite ? '取消收藏这幅作品' : '收藏这幅作品'}
                >
                  <Heart size={19} fill={favorite ? 'currentColor' : 'none'} />
                </button>
              )}
              <button
                className="button button-outline detail-share"
                onClick={share}
                aria-label="复制作品链接"
              >
                <Copy size={17} />
              </button>
            </div>
            {accessible && item.type === 'dynamic' && (
              <button
                className="button button-outline full-button video-download"
                disabled={!!downloadBusy}
                onClick={() => download('mp4')}
              >
                {downloadBusy === 'mp4' ? (
                  <LoaderCircle size={17} className="spin" />
                ) : (
                  <Film size={17} />
                )}
                下载循环视频 MP4 <Download size={15} />
              </button>
            )}
            <p className="purchase-footnote">
              {mode === 'demo' && '体验模式不扣款。'}
              {user
                ? '作品会保存到你的账号，可在「我拥有的」中再次下载。'
                : '游客记录与当前浏览器关联，登录后可保存到账号。'}
            </p>
            {!user && (
              <button className="detail-login-link" onClick={onLogin}>
                <UserRound size={16} />
                登录后，收藏与作品可跨设备查看 <ArrowUpRight size={15} />
              </button>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
}
function InfoDialog({ info, close }: { info: Exclude<Info, null>; close: () => void }) {
  const content = {
    about: {
      eyebrow: '关于幕间',
      title: '美好的日常，从一屏开始。',
      body: (
        <>
          <p>
            幕间，是屏幕亮起与熄灭之间的一点灵感。我们从光线、色彩和自然中寻找美，让一张壁纸成为你与日常相处的另一种方式。
          </p>
          <h3>不追逐数量，认真对待每一幅。</h3>
          <p>
            温暖的流光、安静的山海、恰到好处的留白。每一幅作品都有自己的情绪，也为时钟、图标和你的目光留出空间。
          </p>
          <h3>让喜欢，陪你久一点。</h3>
          <p>
            高清原图保留细腻的色彩与纹理，静态与动态带来不同的感受。不必寻找别人眼中的完美，选一张让自己心动的就好。
          </p>
          <div className="info-signature">
            <Aperture size={26} />
            <span>
              幕间 MUJIAN
              <br />
              <small>为每一次点亮而作。</small>
            </span>
          </div>
        </>
      ),
    },
    guide: {
      eyebrow: '使用指南',
      title: '让喜欢，住进屏幕里。',
      body: (
        <>
          <h3>下载壁纸</h3>
          <p>
            打开作品详情，选择「下载高清 PNG」。需要解锁的作品请先完成解锁。在 iPhone
            下载后，可以从「文件」打开图片，通过分享菜单保存到「照片」。
          </p>
          <h3>设置锁屏或主屏</h3>
          <p>
            在 iPhone 的「设置 → 墙纸 →
            添加新墙纸」中选择下载的图片，再按喜好调整位置与缩放。预览中的时钟和图标不会出现在原图里。
          </p>
          <h3>使用动态作品</h3>
          <p>
            动态作品包含 4 秒循环的 MP4 视频和高清 PNG 原图。MP4 不能直接设为 iPhone
            动态锁屏，需要另行转换为兼容的 Live Photo。我们目前不提供原生 Live
            Photo，转换效果取决于工具、设备和系统，不保证转换后可用。
          </p>
          <h3>再次下载</h3>
          <p>
            登录后，在右上角账号菜单中打开「我拥有的」即可再次下载。使用同一账号登录其他设备，也能查看收藏与已拥有作品。未登录时的记录与当前浏览器关联，不会自动同步；登录时，本机收藏和已解锁作品会合并到账号。
          </p>
        </>
      ),
    },
    license: {
      eyebrow: '授权说明',
      title: '喜欢原创，也尊重原创。',
      body: (
        <>
          <h3>个人使用</h3>
          <p>
            免费或解锁的作品可用于你自己的手机、平板和电脑壁纸，也可以为个人使用进行裁剪、缩放和色彩调整。
          </p>
          <h3>请勿转售与分发</h3>
          <p>
            请勿转售原文件、批量分发或上传到其他素材库。个人使用授权不包含品牌包装、广告或其他商业用途，也不转移作品的著作权。
          </p>
        </>
      ),
    },
    privacy: {
      eyebrow: '隐私说明',
      title: '简单一点，安心一点。',
      body: (
        <>
          <h3>收藏与解锁记录</h3>
          <p>
            收藏仅在登录后使用，并保存在账号中。创建账号需要邮箱和昵称，用于登录及识别你的账号；我们不保存明文密码。登录后，收藏和已拥有作品会关联账号。我们不使用第三方分析脚本。
          </p>
          <h3>支付信息</h3>
          <p>
            体验模式不会收取费用。需要付款时，结算由 Stripe
            提供，我们不接收或保存银行卡号，只保留用于核实订单和解锁作品的必要记录。
          </p>
          <h3>清除网站数据</h3>
          <p>
            清除浏览器数据可能移除游客记录，但不会删除账号中已保存的收藏和作品，重新登录即可查看。退出登录后，页面不会继续显示该账号的个人记录。请妥善保管登录密码及下载的文件。
          </p>
        </>
      ),
    },
  }[info];
  return (
    <Dialog onClose={close} className="info-dialog" title={content.title}>
      <div className="section-kicker">{content.eyebrow}</div>
      <h2>{content.title}</h2>
      <div className="info-body">{content.body}</div>
      <button className="button button-dark" onClick={close}>
        继续发现美好 <ArrowRight size={16} />
      </button>
    </Dialog>
  );
}

createRoot(document.getElementById('root')!).render(<App />);
